import React, { useContext, useEffect, useRef, useState } from 'react'
import { Redirect } from 'react-router-dom'
import trainingplansApi from '../../api/trainingplans'
import OrderByTitle from '../../components/OrderByTitle'
import Switch from '../../components/Switch'
import {
  Heading1Bold,
  Heading5Bold,
  Paragraph2,
} from '../../design-system/typography'
import { Button, Line, PageWrapper } from '../../design-system/utils'
import OrderDirection from '../../types/OrderDirection'
import TrainingPlan from '../../types/TrainingPlan'
import gridTitles from './constants'
import {
  ActionsWrapper,
  DownloadLink,
  FileInput,
  TrainingplansGrid,
  UploadMessage,
  UploadMessageWrapper,
  UploadProgressWrapper,
  ProgressBar,
  ProgressFill,
  ToggleWrapper,
} from './style'
import dateConverter from '../../utils/DateConverter'
import createPlanName from '../../utils/CreatePlanName'
import distanceTokm from '../../utils/DistanceToKm'
import Loader from '../../components/Loader'
import isValidPlanName from '../../utils/ValidatePlanName'
import withAuthorization from '../../components/HOC/withAuthorization'
import { AuthContext } from '../Auth/AuthProvider'

function TrainingPlans() {
  const MAX_FILES_PER_UPLOAD = 500
  const BATCH_SIZE = 20

  const [orderBy, setOrderBy] = useState('')
  const [orderDirection, setOrderDirection] = useState(OrderDirection.DESC)
  const [plans, setPlans] = useState<TrainingPlan[]>([])
  const [unauthorized, setUnauthorized] = useState(false)
  const [loading, setLoading] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')
  const [statusIsError, setStatusIsError] = useState(false)
  const [globalUploadLocked, setGlobalUploadLocked] = useState(false)
  const [cancelRequested, setCancelRequested] = useState(false)
  const [updateExistingPlans, setUpdateExistingPlans] = useState(false)
  const cancelRequestedRef = useRef(false)
  const { loggedInUser } = useContext(AuthContext)
  const [queueProgress, setQueueProgress] = useState<{
    total: number
    processed: number
    currentFile: string
    batchIndex: number
    totalBatches: number
    successful: number
    failed: number
  } | null>(null)

  const setOrder = (orderField: string, direction: OrderDirection) => {
    setOrderBy(orderField)
    setOrderDirection(direction)
    if (direction === OrderDirection.ASC) {
      setPlans((prevPlans) =>
        prevPlans.sort((a, b) => (a[orderField] > b[orderField] ? 1 : -1))
      )
    } else {
      setPlans((prevPlans) =>
        prevPlans.sort((a, b) => (a[orderField] > b[orderField] ? -1 : 1))
      )
    }
  }

  const getPlanTemplates = async () => {
    trainingplansApi
      .getAllTrainingPlans()
      .then((res) => {
        const formattedPlans = res.data.map((plan) => ({
          ...plan,
          name: createPlanName(
            plan.plannedWeeks,
            plan.targetDistance,
            plan?.level
          ),
          formattedDistance: distanceTokm(plan.targetDistance),
        }))
        setPlans(formattedPlans)
      })
      .catch((err) => {
        if (err.response.status === 401 || err.response.status === 403) {
          setUnauthorized(true)
        }
      })
  }

  useEffect(() => {
    getPlanTemplates()
  }, [])

  const resetCancelState = () => {
    cancelRequestedRef.current = false
    setCancelRequested(false)
  }

  const downloadTemplate = () => {
    trainingplansApi.getTemplate().then((res) => {
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `trainingplan_template.csv`)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
    })
  }

  const uploadFiles = async (
    files: FileList,
    shouldUpdateExistingPlans: boolean
  ) => {
    setStatusMessage('')
    setStatusIsError(false)
    resetCancelState()
    const successfulUploads = []
    const failedUploads = []

    const filesArray = Array.from(files) as File[]
    if (filesArray.length > MAX_FILES_PER_UPLOAD) {
      setStatusIsError(true)
      setStatusMessage(
        `You can upload up to ${MAX_FILES_PER_UPLOAD} files at once.`
      )
      return
    }

    setLoading(true)
    setGlobalUploadLocked(true)

    const totalBatches = Math.ceil(filesArray.length / BATCH_SIZE)
    setQueueProgress({
      total: filesArray.length,
      processed: 0,
      currentFile: '',
      batchIndex: 1,
      totalBatches,
      successful: 0,
      failed: 0,
    })

    try {
      await trainingplansApi.startUploadQueue()
    } catch (err) {
      // If queue start endpoint is unavailable, continue with upload attempt.
    }

    let processed = 0
    let successfulCount = 0
    let failedCount = 0

    const batches = Array.from({ length: totalBatches }, (_, index) =>
      filesArray.slice(index * BATCH_SIZE, (index + 1) * BATCH_SIZE)
    )

    const processFile = async (file: File, batchIndex: number) => {
      setQueueProgress({
        total: filesArray.length,
        processed,
        currentFile: file.name,
        batchIndex: batchIndex + 1,
        totalBatches,
        successful: successfulCount,
        failed: failedCount,
      })
      try {
        if (!isValidPlanName(file.name)) {
          throw new Error(
            `Incorrect Plan Name: ${file.name}, please rename your file(s) to match the following pattern: trainingplan_20w_marathon_level1.csv`
          )
        }
        await trainingplansApi.uploadPlan(file, shouldUpdateExistingPlans)
        successfulUploads.push(file.name)
        successfulCount += 1
      } catch (err) {
        const uploadError =
          err?.response && err.response.data ? err.response.data : err?.message
        failedUploads.push({
          fileName: file.name,
          errorMessage: uploadError || 'Upload failed',
        })
        failedCount += 1
      }
      processed += 1
      setQueueProgress({
        total: filesArray.length,
        processed,
        currentFile: file.name,
        batchIndex: batchIndex + 1,
        totalBatches,
        successful: successfulCount,
        failed: failedCount,
      })
    }

    await batches.reduce((batchChain, batch, batchIndex) => {
      return batchChain.then(() => {
        if (cancelRequestedRef.current) {
          return Promise.resolve()
        }
        return batch.reduce((fileChain, file) => {
          return fileChain.then(() => processFile(file, batchIndex))
        }, Promise.resolve())
      })
    }, Promise.resolve())

    setQueueProgress({
      total: filesArray.length,
      processed: filesArray.length,
      currentFile: '',
      batchIndex: totalBatches,
      totalBatches,
      successful: successfulUploads.length,
      failed: failedUploads.length,
    })

    try {
      await trainingplansApi.notifyUploadSummary({
        successful: successfulUploads,
        failed: failedUploads.map(({ fileName }) => fileName),
        failedDetails: failedUploads.map(({ fileName, errorMessage }) => ({
          fileName,
          errorMessage,
        })),
        email: loggedInUser?.email,
        cancelled: cancelRequestedRef.current,
      })
    } catch (err) {
      // Email notification is best-effort.
    }

    try {
      await trainingplansApi.finishUploadQueue()
    } catch (err) {
      // If queue finish endpoint is unavailable, release local UI lock anyway.
    }

    setLoading(false)
    setGlobalUploadLocked(false)
    setQueueProgress(null)
    const email = loggedInUser?.email || 'your email'
    if (cancelRequestedRef.current) {
      setStatusIsError(false)
      setStatusMessage(`Upload cancelled. Details were sent to ${email}.`)
    } else if (failedUploads.length > 0) {
      setStatusIsError(true)
      setStatusMessage(
        `Upload finished with errors. Details were sent to ${email}.`
      )
    } else {
      setStatusIsError(false)
      setStatusMessage(
        `Upload finished successfully. Details were sent to ${email}.`
      )
    }

    if (successfulUploads.length > 0) {
      getPlanTemplates()
    }
  }

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) {
      return
    }
    if (loading) {
      return
    }
    setStatusMessage('')
    setStatusIsError(false)
    setGlobalUploadLocked(false)
    try {
      const res = await trainingplansApi.getUploadQueueStatus()
      const locked = Boolean(res?.data?.locked)
      if (locked) {
        setGlobalUploadLocked(true)
        setStatusIsError(true)
        setStatusMessage(
          'Upload queue is currently in use by another admin. Please try again shortly.'
        )
        return
      }
    } catch (err) {
      // If status endpoint is unavailable, allow upload attempt.
    }
    uploadFiles(e.target.files, updateExistingPlans)
  }

  const onCancelUpload = () => {
    cancelRequestedRef.current = true
    setCancelRequested(true)
  }

  const isUploadDisabled = loading
  const progressPercent =
    queueProgress && queueProgress.total > 0
      ? Math.round((queueProgress.processed / queueProgress.total) * 100)
      : 0

  if (unauthorized) {
    return <Redirect to="/login" />
  }

  return (
    <PageWrapper>
      <Heading1Bold>Training plan</Heading1Bold>
      <ActionsWrapper>
        <div>
          <Heading5Bold>Training plan in CSV</Heading5Bold>
          <DownloadLink onClick={downloadTemplate}>
            Download a template
          </DownloadLink>
        </div>
        {loading ? (
          <Button secondary onClick={onCancelUpload}>
            Cancel
          </Button>
        ) : (
          <Button secondary as="label" disabled={isUploadDisabled}>
            <FileInput
              type="file"
              accept=".csv"
              onChange={onUpload}
              multiple
              name="file[]"
              disabled={isUploadDisabled}
            />
            Upload
          </Button>
        )}
        <ToggleWrapper>
          <Switch
            checked={updateExistingPlans}
            onChange={() => setUpdateExistingPlans((prev) => !prev)}
          />
          <Paragraph2>Update existing plans</Paragraph2>
        </ToggleWrapper>
      </ActionsWrapper>
      {statusMessage ? (
        <UploadMessageWrapper>
          <UploadMessage error={statusIsError}>
            <Paragraph2>{statusMessage}</Paragraph2>
          </UploadMessage>
        </UploadMessageWrapper>
      ) : null}
      {loading && queueProgress ? (
        <UploadMessageWrapper>
          <UploadProgressWrapper>
            <Heading5Bold>
              Uploading batch {queueProgress.batchIndex} of{' '}
              {queueProgress.totalBatches}
            </Heading5Bold>
            <Paragraph2>
              {queueProgress.processed}/{queueProgress.total} processed •{' '}
              {queueProgress.successful} succeeded • {queueProgress.failed}{' '}
              failed
            </Paragraph2>
            {cancelRequested ? (
              <Paragraph2>
                Cancel requested. Finishing current batch…
              </Paragraph2>
            ) : null}
            {queueProgress.currentFile ? (
              <Paragraph2>Current: {queueProgress.currentFile}</Paragraph2>
            ) : null}
            <Loader />
            <ProgressBar>
              <ProgressFill progress={progressPercent} />
            </ProgressBar>
          </UploadProgressWrapper>
        </UploadMessageWrapper>
      ) : null}
      <Line />
      <TrainingplansGrid>
        {gridTitles.map((title) => (
          <OrderByTitle
            title={title.title}
            field={title.field}
            isChosen={title.field === orderBy}
            chosenDirection={orderDirection}
            setOrder={setOrder}
            key={title.field}
          />
        ))}
        {plans.map((plan) => (
          <React.Fragment key={plan.id}>
            <Paragraph2>{plan.name ? plan.name : '-'}</Paragraph2>
            <Paragraph2>{plan.free ? 'Free' : 'Member'}</Paragraph2>
            <Paragraph2>
              {plan.createdAt ? dateConverter(plan.createdAt) : '-'}
            </Paragraph2>
            <Paragraph2>
              {plan.formattedDistance ? `${plan.formattedDistance} km` : '-'}
            </Paragraph2>
            <Paragraph2>
              {plan.plannedWeeks ? plan.plannedWeeks : '-'}
            </Paragraph2>
            <Paragraph2>{plan?.level || '-'}</Paragraph2>
          </React.Fragment>
        ))}
      </TrainingplansGrid>
      {/* @deprecated */}
      {/* {overwriteFile && (
        <OverWriteModal
          onClose={() => setOverwriteFile(null)}
          onUploadFile={() => uploadFiles(overwriteFile)}
        />
      )} */}
    </PageWrapper>
  )
}

export default withAuthorization(TrainingPlans)
